import React, { useState, useEffect } from 'react';
import {
  FaShoppingCart,
  FaSearch,
  FaLeaf,
  FaDrumstickBite,
  FaPlus,
  FaMinus,
  FaTimes,
  FaCheck,
  FaInfoCircle,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { toast, ToastContainer } from 'react-toastify';
import Cookies from 'js-cookie';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
const REACT_APP_RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

// Guest data functions
const getGuestData = async () => {
  const accessToken = Cookies.get('access_token');
  if (!accessToken) return null;

  try {
    const guestId = Cookies.get('guest_id') || getGuestIdFromToken(accessToken);
    if (!guestId) return null;

    const profileResponse = await fetch(`${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!profileResponse.ok) {
      throw new Error(`Profile API Error: ${profileResponse.statusText}`);
    }

    const profileData = await profileResponse.json();

    const isCorporateGuest = profileData.companyid && profileData.companyname;

    const isDecrypted = profileData.emailid && profileData.emailid.length < 50;

    if (isDecrypted) {
      return {
        first_name: profileData.firstname || '',
        last_name: profileData.lastname || '',
        email: profileData.emailid || '',
        phone_number: profileData.phonenumber || '',
        country_code: '+91',
        client_type: profileData.clienttype || 'Leisure',
        company_id: profileData.companyid || '',
        company_name: profileData.companyname || '',
        is_corporate: isCorporateGuest,
      };
    }

    const decryptedFields = await decryptUserData({
      email: profileData.emailid,
      phone_number: profileData.phonenumber,
      country_code: profileData.countrycode,
    });

    return {
      first_name: profileData.firstname || '',
      last_name: profileData.lastname || '',
      email: decryptedFields.email || profileData.emailid || '',
      phone_number: decryptedFields.phone_number || profileData.phonenumber || '',
      country_code: '+91',
      client_type: profileData.clienttype || 'Leisure',
      company_id: profileData.companyid || '',
      company_name: profileData.companyname || '',
      is_corporate: isCorporateGuest,
    };
  } catch (error) {
    console.error('Error fetching guest data:', error);
    return null;
  }
};

const getGuestIdFromToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub;
  } catch (e) {
    console.error('Error parsing token:', e);
    return null;
  }
};

const decryptUserData = async (encryptedData) => {
  try {
    if (encryptedData.email && encryptedData.email.length < 50) {
      return encryptedData;
    }

    const accessToken = Cookies.get('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }

    const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        encrypted_fields: {
          email: encryptedData.email,
          phone_number: encryptedData.phone_number,
          country_code: encryptedData.country_code,
        },
      }),
    });

    if (response.status === 401) {
      return encryptedData;
    }

    if (!response.ok) {
      throw new Error(`Decryption failed: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Decryption error:', error);
    return encryptedData;
  }
};

function Food() {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [guestData, setGuestData] = useState(null);
  const [isCorporateGuest, setIsCorporateGuest] = useState(false);
  // Blocks a second submit while the first order is still in flight. Without
  // this, a double click on Order Now placed the whole order twice.
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails')) || {};
  const bookingId =
    bookingDetails.primaryBooking?.booking_id || bookingDetails.bookings?.[0]?.booking_id;

  // Fetch guest data on component mount
  useEffect(() => {
    const fetchGuestData = async () => {
      const guestInfo = await getGuestData();
      setGuestData(guestInfo);
      setIsCorporateGuest(guestInfo?.is_corporate || false);
    };

    fetchGuestData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [itemsRes, categoriesRes, subcategoriesRes] = await Promise.all([
          fetch(`${BQ_BASE_URL}/bq/api/items/`),
          fetch(`${BQ_BASE_URL}/bq/api/categories/`),
          fetch(`${BQ_BASE_URL}/bq/api/subcategories/`),
        ]);

        if (!itemsRes.ok || !categoriesRes.ok || !subcategoriesRes.ok) {
          throw new Error('Failed to fetch data');
        }

        const itemsData = await itemsRes.json();
        const categoriesData = await categoriesRes.json();
        const subcategoriesData = await subcategoriesRes.json();

        setMenuItems(itemsData.items);
        setCategories(categoriesData);
        setSubcategories(subcategoriesData);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getCategoryIdFromSubCategoryId = (subCategoryId) => {
    const subcategory = subcategories.find((sub) => sub.id === subCategoryId);
    return subcategory ? subcategory.categoryid : null;
  };

  const handleAddToCart = (item) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((cartItem) => cartItem.id === item.id);
      if (existingItem) {
        return prevCart.map((cartItem) =>
          cartItem.id === item.id ? { ...cartItem, quantity: cartItem.quantity + 1 } : cartItem
        );
      }
      return [...prevCart, { ...item, quantity: 1 }];
    });
    toast.success(`${item.title} added to cart!`);
  };

  const handleRemoveFromCart = (id) => {
    setCart(cart.filter((item) => item.id !== id));
  };

  const handleIncreaseQuantity = (id) => {
    setCart((prevCart) =>
      prevCart.map((item) => (item.id === id ? { ...item, quantity: item.quantity + 1 } : item))
    );
  };

  const handleDecreaseQuantity = (id) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === id && item.quantity > 1 ? { ...item, quantity: item.quantity - 1 } : item
      )
    );
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const totalAmount = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  // ✅ Calculate taxes based purely on API values - NO HARDCODING
  const calculateTaxes = () => {
    return cart.reduce((sum, item) => {
      // If value_type is 'P' (Percentage), calculate percentage of price
      if (item.value_type === 'P') {
        return sum + (item.price * item.quantity * item.value) / 100;
      }
      // If value_type is 'F' (Fixed), add fixed amount per item
      else if (item.value_type === 'F') {
        return sum + item.value * item.quantity;
      }
      // If no value_type, use 0 tax (don't hardcode)
      return sum + 0;
    }, 0);
  };

  const taxes = calculateTaxes();
  const finalTotal = totalAmount + taxes;

  // ✅ Get detailed tax breakdown for a single item - purely from API
  const getItemTaxBreakdown = (item) => {
    const quantity = item.quantity || 1;
    const price = item.price || 0;
    const value = item.value || 0;
    const valueType = item.value_type || '';

    if (valueType === 'P') {
      const taxAmount = (price * quantity * value) / 100;
      return {
        type: 'percentage',
        rate: value,
        taxAmount: taxAmount,
        cgst: taxAmount / 2,
        sgst: taxAmount / 2,
        fixedTax: 0,
      };
    } else if (valueType === 'F') {
      const taxAmount = value * quantity;
      return {
        type: 'fixed',
        rate: value,
        taxAmount: taxAmount,
        cgst: 0,
        sgst: 0,
        fixedTax: taxAmount,
      };
    } else {
      // No tax if value_type is not specified
      return {
        type: 'none',
        rate: 0,
        taxAmount: 0,
        cgst: 0,
        sgst: 0,
        fixedTax: 0,
      };
    }
  };

  // ✅ Calculate total tax breakdown across all items
  const calculateTotalTaxBreakdown = () => {
    let totalCGST = 0;
    let totalSGST = 0;
    let totalFixedTax = 0;
    let totalPercentageTax = 0;

    cart.forEach((item) => {
      const breakdown = getItemTaxBreakdown(item);
      totalCGST += breakdown.cgst;
      totalSGST += breakdown.sgst;
      totalFixedTax += breakdown.fixedTax;
      totalPercentageTax += breakdown.taxAmount;
    });

    return {
      cgst: totalCGST,
      sgst: totalSGST,
      fixedTax: totalFixedTax,
      totalPercentageTax: totalPercentageTax,
      totalTax: totalCGST + totalSGST + totalFixedTax,
    };
  };

  const taxBreakdown = calculateTotalTaxBreakdown();

  // ✅ Get tax display text for an item - purely from API
  const getTaxDisplay = (item) => {
    if (item.value_type === 'P') {
      return `${item.value}%`;
    } else if (item.value_type === 'F') {
      return `₹${item.value}`;
    } else {
      return 'No Tax';
    }
  };

  const handleCheckout = async () => {
    if (!bookingId) {
      alert('Booking ID not found. Please ensure you have a valid booking.');
      return;
    }

    // Guard against duplicate orders from a double click or an impatient
    // second tap while the network request is still running.
    if (isPlacingOrder) {
      return;
    }
    setIsPlacingOrder(true);

    const items = cart.map((item) => ({
      foodid: item.id,
      quantity: item.quantity,
    }));

    try {
      const orderResponse = await fetch(`${BQ_BASE_URL}/bq/api/orders/`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ bookingid: bookingId, items }),
      });

      const orderData = await orderResponse.json();
      console.log('Order Response:', orderData);

      if (!orderResponse.ok) {
        throw new Error('Failed to place order');
      }

      const food_order_id = orderData.order_id;

      // POST /orders/ already creates the billing row and the order_detail
      // rows for this food order, and links them via foodorder.billingid.
      // Calling create_food_order_billing afterwards created a SECOND billing
      // row plus a duplicate set of order_detail rows, so the guest was
      // charged twice on the room folio.
      //
      // Resolution order:
      //   1. billing_id straight off the order response, when present
      //   2. read it back from the order (works with the current backend)
      //   3. only if both fail, fall back to the old billing call
      let billingData = null;

      if (orderData.billing_id) {
        billingData = { billingid: orderData.billing_id };
      } else {
        try {
          const orderLookup = await fetch(`${BQ_BASE_URL}/bq/api/orders/${food_order_id}`, {
            headers: { accept: 'application/json' },
          });
          if (orderLookup.ok) {
            const orderRow = await orderLookup.json();
            if (orderRow?.billingid) {
              billingData = { billingid: orderRow.billingid };
            }
          }
        } catch (lookupError) {
          console.warn('Could not read billing from the order:', lookupError);
        }
      }

      // Legacy fallback only. Reached when the order somehow has no billing
      // row yet, so it cannot duplicate an existing one.
      if (!billingData?.billingid) {
        const billingResponse = await fetch(
          `${BQ_BASE_URL}/bq/api/create_food_order_billing?bookingid=${bookingId}&food_order_id=${food_order_id}`,
          {
            method: 'POST',
            headers: {
              accept: 'application/json',
            },
          }
        );

        billingData = await billingResponse.json();

        if (!billingResponse.ok) {
          throw new Error('Failed to create billing');
        }
      }

      localStorage.setItem('billingDetails', JSON.stringify(billingData));
      console.log('Billing resolved:', billingData);

      toast.success('Order placed & billing done successfully!');
      setCart([]);
      setIsSidebarOpen(false);
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Error:', error);
      alert('Checkout failed. Try again.');
    } finally {
      setIsPlacingOrder(false);
    }
  };

  useEffect(() => {
    const loadRazorpayScript = () => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => setRazorpayLoaded(true);
      script.onerror = () => {
        toast.error('Error loading Razorpay script. Please try again.');
      };
      document.body.appendChild(script);
    };

    loadRazorpayScript();
  }, []);

  const handlePaymentOption = async (paymentMethod) => {
    if (!razorpayLoaded) {
      toast.error('Razorpay script is not loaded yet.');
      return;
    }

    if (paymentMethod === 'payNow') {
      try {
        const billingDetails = JSON.parse(localStorage.getItem('billingDetails'));
        if (!billingDetails || !billingDetails.billingid) {
          toast.error('Billing details not found. Please try again.');
          return;
        }

        const billingId = billingDetails.billingid;
        const primaryBooking = bookingDetails.primaryBooking || bookingDetails.bookings?.[0];

        const apiUrl = `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order?billingid=${billingId}&bookingid=${bookingId}`;

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        });

        if (!response.ok) throw new Error('Failed to create payment order');

        const orderData = await response.json();

        if (orderData && orderData.razorpay_order_id) {
          const options = {
            key: REACT_APP_RAZORPAY_KEY,
            amount: orderData.amount,
            currency: 'INR',
            name: 'Pagoda Hotel',
            description: 'Food Service',
            order_id: orderData.razorpay_order_id,
            handler: async function (response) {
              const paymentDetails = {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              };

              try {
                const verificationResponse = await fetch(
                  `${BQ_BASE_URL}/bq/api/razorpay/verify_payment`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Accept: 'application/json',
                    },
                    body: JSON.stringify(paymentDetails),
                  }
                );

                if (verificationResponse.ok) {
                  toast.success('Payment successful and verified!');
                  window.location.href =
                    '/reservation/options/booking/welcome/food-service/succesful';
                } else {
                  toast.error('Payment verification failed.');
                }
              } catch (error) {
                console.error('Verification error:', error);
                toast.error('Error verifying payment. Please try again.');
              }
            },
            prefill: {
              name: primaryBooking?.guest_name || 'Guest',
              email: primaryBooking?.guest_email || '',
              contact: primaryBooking?.guest_phone?.toString() || '',
            },
            theme: {
              color: '#3399cc',
            },
          };

          const rzp = new window.Razorpay(options);
          rzp.open();
        }
      } catch (error) {
        console.error('Payment error:', error);
        toast.error('Error processing payment. Please try again.');
      }
    } else if (paymentMethod === 'payLater') {
      toast.info('Payment deferred. Please pay at the front desk.');
      localStorage.removeItem('selectedService');
      localStorage.removeItem('billingDetails');
      setShowPaymentModal(false);
    } else if (paymentMethod === 'billToCompany') {
      toast.info(
        `Bill to company option selected. Charges will be billed to ${guestData?.company_name || 'your company'}.`
      );
      localStorage.removeItem('selectedService');
      localStorage.removeItem('billingDetails');
      setShowPaymentModal(false);
    }
  };

  const filteredItems = menuItems.filter((item) => {
    const categoryid = getCategoryIdFromSubCategoryId(item.subCategoryId);
    const matchesCategory = selectedCategory ? categoryid === selectedCategory : true;
    const matchesSubcategory = selectedSubcategory
      ? item.subCategoryId === selectedSubcategory
      : true;
    const matchesSearch = item.title.toLowerCase().includes(search.toLowerCase());

    return matchesCategory && matchesSubcategory && matchesSearch;
  });

  if (loading) return <p className="text-center text-lg font-semibold">Loading...</p>;
  if (error) return <p className="text-center text-red-500">{error}</p>;

  return (
    <div className="min-h-screen bg-gray-100 p-6 flex flex-col items-center ">
      <Link
        to="/reservation/options/booking/welcome/room-service"
        className="absolute top-24 left-8 text-black hover:text-gray-800 transition font-semibold text-2xl"
      >
        <IoMdArrowRoundBack size={32} />
      </Link>

      <div className="fixed right-16 top-28 z-40">
        <button
          onClick={toggleSidebar}
          className="relative text-black text-2xl bg-white p-2 rounded-full shadow-lg hover:shadow-xl transition"
        >
          <FaShoppingCart size={36} />
          {cart.length > 0 && (
            <span className="absolute -top-1 -right-2 bg-red-500 text-white text-sm font-semibold px-2 rounded-full py-1">
              {cart.length}
            </span>
          )}
        </button>
      </div>

      <header className="text-white p-6 text-center">
        <h1 className="text-3xl font-bold text-black mt-">Food Menu</h1>
        <p className="text-md font-semibold text-gray-700 mb-1">
          Explore a variety of delicious meals, from hearty main courses to sweet desserts.
        </p>
        <hr className="border border-gray-300 w-2/3 mx-auto mb-3" />
      </header>

      <div className="relative w-full max-w-7xl my-6">
        <input
          type="text"
          placeholder="Search for food..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-12 py-2.5 border border-gray-300 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-gray-400"
        />
        <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500" />
      </div>

      <div className="flex gap-4 overflow-x-auto mb-4">
        <button
          className={`px-4 py-2 rounded-lg ${
            selectedCategory === null ? 'bg-black text-white' : 'bg-gray-200'
          }`}
          onClick={() => {
            setSelectedCategory(null);
            setSelectedSubcategory(null);
          }}
        >
          All
        </button>
        {categories.map((category) => (
          <button
            key={category.id}
            className={`px-4 py-2 rounded-lg ${
              selectedCategory === category.id ? 'bg-black text-white' : 'bg-gray-200'
            }`}
            onClick={() => {
              setSelectedCategory(category.id);
              setSelectedSubcategory(null);
            }}
          >
            {category.name}
          </button>
        ))}
      </div>

      {selectedCategory && (
        <div className="flex gap-4 overflow-x-auto mb-4">
          <button
            className={`px-4 py-2 rounded-lg ${
              selectedSubcategory === null ? 'bg-black text-white' : 'bg-gray-200'
            }`}
            onClick={() => setSelectedSubcategory(null)}
          >
            All
          </button>
          {subcategories
            .filter((sub) => sub.categoryid === selectedCategory)
            .map((sub) => (
              <button
                key={sub.id}
                className={`px-4 py-2 rounded-lg ${
                  selectedSubcategory === sub.id ? 'bg-black text-white' : 'bg-gray-200'
                }`}
                onClick={() => {
                  setSelectedSubcategory(sub.id);
                }}
              >
                {sub.name}
              </button>
            ))}
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-2 max-w-7xl">
        {filteredItems.map((item) => {
          const isInCart = cart.some((cartItem) => cartItem.id === item.id);
          return (
            <div
              key={item.id}
              className="relative rounded-2xl overflow-hidden shadow-lg bg-white hover:shadow-xl transition-shadow flex flex-col"
            >
              <img src={item.image} alt={item.title} className="w-full h-60 object-cover" />
              <div className="absolute top-2 right-2 bg-white p-1 rounded-full shadow-md">
                {item.foodType === 'Veg' ? (
                  <FaLeaf className="text-green-600 text-xl" />
                ) : (
                  <FaDrumstickBite className="text-red-600 text-xl" />
                )}
              </div>
              <div className="p-4 flex flex-col flex-grow">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold">{item.title}</h2>
                  <p
                    className={`text-xs font-semibold px-3 py-1 rounded-lg ${
                      item.isActive ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                    }`}
                  >
                    {item.isActive ? 'Available' : 'Unavailable'}
                  </p>
                </div>
                <p className="text-gray-700 text-sm mb-3">{item.description}</p>
                <div className="flex flex-wrap gap-2 mt-auto mb-2">
                  {item.mealTypes.map((meal) => (
                    <span
                      key={meal}
                      className="bg-gray-200 text-gray-700 text-xs font-semibold px-3 py-1 rounded-full"
                    >
                      {meal}
                    </span>
                  ))}
                </div>
                <p className="text-lg font-bold mt-auto">₹{item.price}</p>
                <p className="text-xs text-gray-500">
                  {item.value_type === 'P' ? (
                    <span>+ {item.value}% Tax</span>
                  ) : item.value_type === 'F' ? (
                    <span>+ ₹{item.value} Fixed Tax</span>
                  ) : (
                    <span className="text-gray-400">No Tax</span>
                  )}
                </p>
              </div>
              <div className="px-4 py-2">
                <button
                  onClick={() => handleAddToCart(item)}
                  disabled={!item.isActive}
                  className={`w-full px-4 py-2 rounded-lg flex justify-center items-center gap-2 ${
                    isInCart
                      ? 'bg-green-600 text-white'
                      : item.isActive
                        ? 'bg-black text-white'
                        : 'bg-gray-300 text-gray-700 font-semibold cursor-not-allowed'
                  }`}
                >
                  {isInCart ? (
                    <>
                      <FaCheck /> Added
                    </>
                  ) : (
                    <>
                      <FaShoppingCart /> Add to Cart
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {isSidebarOpen && (
        <div className="fixed right-0 top-0 h-full w-1/3 bg-white shadow-2xl p-6 overflow-y-auto z-50 transform transition-transform duration-300 ease-in-out flex flex-col">
          <button
            onClick={toggleSidebar}
            className="text-gray-700 text-2xl absolute top-4 right-4 hover:text-gray-900"
          >
            <FaTimes />
          </button>

          <h2 className="text-2xl font-bold text-gray-800 mb-6">Your Cart</h2>

          <div className="flex-grow overflow-y-auto px-4">
            {cart.length > 0 ? (
              cart.map((item) => {
                const itemTax = getItemTaxBreakdown(item);
                return (
                  <div key={item.id} className="flex items-center gap-4 border-b pb-6 mb-4">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-20 h-20 rounded-lg shadow-md object-cover"
                    />
                    <div className="flex-grow">
                      <h3 className="text-[18px] font-semibold text-gray-900">{item.title}</h3>
                      <p className="text-lg font-semibold text-gray-600">
                        ₹{item.price * item.quantity}
                      </p>
                      <p className="text-xs text-gray-500">
                        {item.value_type === 'P' ? (
                          <span>
                            Tax: {item.value}% (₹{itemTax.taxAmount.toFixed(2)})
                          </span>
                        ) : item.value_type === 'F' ? (
                          <span>
                            Tax: ₹{item.value} fixed (₹{itemTax.taxAmount.toFixed(2)})
                          </span>
                        ) : (
                          <span className="text-gray-400">No Tax</span>
                        )}
                      </p>
                    </div>

                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => handleRemoveFromCart(item.id)}
                        className="mb-3 px-3 py-1 whitespace-nowrap text-sm font-semibold text-white bg-red-500 rounded-md hover:bg-red-700 hover:text-white transition"
                      >
                        Remove Item
                      </button>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDecreaseQuantity(item.id)}
                          className="w-10 h-10 flex items-center justify-center bg-gray-200 rounded-full hover:bg-gray-300"
                        >
                          <FaMinus />
                        </button>
                        <span className="text-lg font-bold">{item.quantity}</span>
                        <button
                          onClick={() => handleIncreaseQuantity(item.id)}
                          className="w-10 h-10 flex items-center justify-center bg-gray-200 rounded-full hover:bg-gray-300"
                        >
                          <FaPlus />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-center text-gray-500">Your cart is empty.</p>
            )}
          </div>

          {cart.length > 0 && (
            <div className="mt-auto border-t pt-4 bg-white">
              {/* Subtotal */}
              <div className="flex justify-between mb-2">
                <span className="text-gray-600 font-semibold">Subtotal:</span>
                <span className="font-semibold">
                  ₹
                  {totalAmount.toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>

              {/* Taxes & Charges - Purely from API */}
              <div className="flex justify-between mb-2 relative group">
                <span className="text-gray-600 font-semibold flex items-center gap-1">
                  Taxes & Charges
                  <span className="text-gray-400 cursor-pointer relative">
                    <FaInfoCircle size={14} />
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-2 px-3 w-56 z-10">
                      <div className="font-semibold mb-1 text-center">Tax Breakdown</div>
                      {taxBreakdown.cgst > 0 && (
                        <div className="flex justify-between">
                          <span>CGST:</span>
                          <span>
                            ₹
                            {taxBreakdown.cgst.toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      )}
                      {taxBreakdown.sgst > 0 && (
                        <div className="flex justify-between">
                          <span>SGST:</span>
                          <span>
                            ₹
                            {taxBreakdown.sgst.toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      )}
                      {taxBreakdown.fixedTax > 0 && (
                        <div className="flex justify-between">
                          <span>Fixed Tax:</span>
                          <span>
                            ₹
                            {taxBreakdown.fixedTax.toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      )}
                      <div className="border-t border-gray-600 mt-1 pt-1 flex justify-between font-semibold">
                        <span>Total Tax:</span>
                        <span>
                          ₹
                          {taxBreakdown.totalTax.toLocaleString('en-IN', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                      {/* Show per-item tax details */}
                      {cart.length > 0 && (
                        <div className="border-t border-gray-600 mt-1 pt-1">
                          <div className="font-semibold text-center text-[10px]">Per Item</div>
                          {cart.map((item, idx) => {
                            const tax = getItemTaxBreakdown(item);
                            return (
                              <div key={idx} className="flex justify-between text-[10px]">
                                <span className="truncate max-w-[100px]">{item.title}:</span>
                                <span>
                                  ₹{tax.taxAmount.toFixed(2)} ({getTaxDisplay(item)})
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </span>
                </span>
                <span className="font-semibold">
                  ₹
                  {taxBreakdown.totalTax.toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>

              {/* Line separator */}
              <hr className="my-2 border-gray-300" />

              {/* Total */}
              <div className="flex justify-between mb-4">
                <span className="text-lg font-bold text-gray-800">Total:</span>
                <span className="text-lg font-bold text-gray-800">
                  ₹
                  {finalTotal.toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>

              <button
                onClick={handleCheckout}
                disabled={isPlacingOrder}
                className="mt-4 w-full bg-black text-white py-3 rounded-lg text-lg font-semibold hover:bg-gray-900 transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isPlacingOrder ? 'Placing order...' : 'Order Now'}
              </button>
            </div>
          )}
        </div>
      )}

      {showPaymentModal && (
        <div className="fixed inset-0 flex justify-center items-center bg-gray-800 bg-opacity-70 z-50">
          <div className="bg-white p-6 rounded-lg w-4/12 text-center shadow-md transform transition-all duration-200">
            <h3 className="text-2xl font-semibold mb-4 text-gray-900">Complete Your Payment</h3>
            <p className="text-md text-gray-600 mb-6">
              Your Food has been successfully Ordered! Please select a payment method to proceed.
            </p>

            <div className="flex justify-center gap-4">
              {/* Pay Later option for leisure guests */}
              {!isCorporateGuest && (
                <button
                  onClick={() => handlePaymentOption('payLater')}
                  className="bg-black text-white px-10 py-2 rounded-md hover:text-gray-200 transition-all duration-200 font-semibold"
                >
                  Pay Later
                </button>
              )}

              {/* Bill to Company option for corporate guests */}
              {isCorporateGuest && (
                <button
                  onClick={() => handlePaymentOption('billToCompany')}
                  className="bg-black text-white px-10 py-2 rounded-md hover:text-gray-200 transition-all duration-200 font-semibold"
                >
                  Bill to Company
                </button>
              )}

              {/* Pay Now option for all guests */}
              <button
                onClick={() => handlePaymentOption('payNow')}
                className="bg-green-600 text-white px-10 py-2 rounded-md hover:text-black transition-all duration-200 font-semibold"
              >
                Pay Now
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
}

export default Food;

// import React, { useState, useEffect } from 'react';
// import {
//   FaShoppingCart,
//   FaSearch,
//   FaLeaf,
//   FaDrumstickBite,
//   FaPlus,
//   FaMinus,
//   FaTimes,
//   FaCheck,
//   FaInfoCircle,
// } from 'react-icons/fa';
// import { Link } from 'react-router-dom';
// import { IoMdArrowRoundBack } from 'react-icons/io';
// import { toast, ToastContainer } from 'react-toastify';
// import Cookies from 'js-cookie';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
// const REACT_APP_RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

// // Guest data functions
// const getGuestData = async () => {
//   const accessToken = Cookies.get('access_token');
//   if (!accessToken) return null;

//   try {
//     const guestId = Cookies.get('guest_id') || getGuestIdFromToken(accessToken);
//     if (!guestId) return null;

//     const profileResponse = await fetch(`${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`, {
//       method: 'GET',
//       headers: {
//         Accept: 'application/json',
//         Authorization: `Bearer ${accessToken}`,
//       },
//     });

//     if (!profileResponse.ok) {
//       throw new Error(`Profile API Error: ${profileResponse.statusText}`);
//     }

//     const profileData = await profileResponse.json();

//     const isCorporateGuest = profileData.companyid && profileData.companyname;

//     const isDecrypted = profileData.emailid && profileData.emailid.length < 50;

//     if (isDecrypted) {
//       return {
//         first_name: profileData.firstname || '',
//         last_name: profileData.lastname || '',
//         email: profileData.emailid || '',
//         phone_number: profileData.phonenumber || '',
//         country_code: '+91',
//         client_type: profileData.clienttype || 'Leisure',
//         company_id: profileData.companyid || '',
//         company_name: profileData.companyname || '',
//         is_corporate: isCorporateGuest,
//       };
//     }

//     const decryptedFields = await decryptUserData({
//       email: profileData.emailid,
//       phone_number: profileData.phonenumber,
//       country_code: profileData.countrycode,
//     });

//     return {
//       first_name: profileData.firstname || '',
//       last_name: profileData.lastname || '',
//       email: decryptedFields.email || profileData.emailid || '',
//       phone_number: decryptedFields.phone_number || profileData.phonenumber || '',
//       country_code: '+91',
//       client_type: profileData.clienttype || 'Leisure',
//       company_id: profileData.companyid || '',
//       company_name: profileData.companyname || '',
//       is_corporate: isCorporateGuest,
//     };
//   } catch (error) {
//     console.error('Error fetching guest data:', error);
//     return null;
//   }
// };

// const getGuestIdFromToken = (token) => {
//   try {
//     const payload = JSON.parse(atob(token.split('.')[1]));
//     return payload.sub;
//   } catch (e) {
//     console.error('Error parsing token:', e);
//     return null;
//   }
// };

// const decryptUserData = async (encryptedData) => {
//   try {
//     if (encryptedData.email && encryptedData.email.length < 50) {
//       return encryptedData;
//     }

//     const accessToken = Cookies.get('access_token');
//     if (!accessToken) {
//       throw new Error('No access token found');
//     }

//     const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/json',
//         Authorization: `Bearer ${accessToken}`,
//       },
//       body: JSON.stringify({
//         encrypted_fields: {
//           email: encryptedData.email,
//           phone_number: encryptedData.phone_number,
//           country_code: encryptedData.country_code,
//         },
//       }),
//     });

//     if (response.status === 401) {
//       return encryptedData;
//     }

//     if (!response.ok) {
//       throw new Error(`Decryption failed: ${response.statusText}`);
//     }

//     return await response.json();
//   } catch (error) {
//     console.error('Decryption error:', error);
//     return encryptedData;
//   }
// };

// function Food() {
//   const [menuItems, setMenuItems] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [search, setSearch] = useState('');
//   const [cart, setCart] = useState([]);
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const [subcategories, setSubcategories] = useState([]);
//   const [selectedCategory, setSelectedCategory] = useState(null);
//   const [selectedSubcategory, setSelectedSubcategory] = useState(null);
//   const [showPaymentModal, setShowPaymentModal] = useState(false);
//   const [razorpayLoaded, setRazorpayLoaded] = useState(false);
//   const [guestData, setGuestData] = useState(null);
//   const [isCorporateGuest, setIsCorporateGuest] = useState(false);

//   const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails')) || {};
//   const bookingId =
//     bookingDetails.primaryBooking?.booking_id || bookingDetails.bookings?.[0]?.booking_id;

//   // Fetch guest data on component mount
//   useEffect(() => {
//     const fetchGuestData = async () => {
//       const guestInfo = await getGuestData();
//       setGuestData(guestInfo);
//       setIsCorporateGuest(guestInfo?.is_corporate || false);
//     };

//     fetchGuestData();
//   }, []);

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const [itemsRes, categoriesRes, subcategoriesRes] = await Promise.all([
//           fetch(`${BQ_BASE_URL}/bq/api/items/`),
//           fetch(`${BQ_BASE_URL}/bq/api/categories/`),
//           fetch(`${BQ_BASE_URL}/bq/api/subcategories/`),
//         ]);

//         if (!itemsRes.ok || !categoriesRes.ok || !subcategoriesRes.ok) {
//           throw new Error('Failed to fetch data');
//         }

//         const itemsData = await itemsRes.json();
//         const categoriesData = await categoriesRes.json();
//         const subcategoriesData = await subcategoriesRes.json();

//         setMenuItems(itemsData.items);
//         setCategories(categoriesData);
//         setSubcategories(subcategoriesData);
//       } catch (error) {
//         setError(error.message);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   const getCategoryIdFromSubCategoryId = (subCategoryId) => {
//     const subcategory = subcategories.find((sub) => sub.id === subCategoryId);
//     return subcategory ? subcategory.categoryid : null;
//   };

//   const handleAddToCart = (item) => {
//     setCart((prevCart) => {
//       const existingItem = prevCart.find((cartItem) => cartItem.id === item.id);
//       if (existingItem) {
//         return prevCart.map((cartItem) =>
//           cartItem.id === item.id ? { ...cartItem, quantity: cartItem.quantity + 1 } : cartItem
//         );
//       }
//       return [...prevCart, { ...item, quantity: 1 }];
//     });
//     toast.success(`${item.title} added to cart!`);
//   };

//   const handleRemoveFromCart = (id) => {
//     setCart(cart.filter((item) => item.id !== id));
//   };

//   const handleIncreaseQuantity = (id) => {
//     setCart((prevCart) =>
//       prevCart.map((item) => (item.id === id ? { ...item, quantity: item.quantity + 1 } : item))
//     );
//   };

//   const handleDecreaseQuantity = (id) => {
//     setCart((prevCart) =>
//       prevCart.map((item) =>
//         item.id === id && item.quantity > 1 ? { ...item, quantity: item.quantity - 1 } : item
//       )
//     );
//   };

//   const toggleSidebar = () => {
//     setIsSidebarOpen(!isSidebarOpen);
//   };

//   const totalAmount = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

//   // ✅ Calculate taxes based purely on API values - NO HARDCODING
//   const calculateTaxes = () => {
//     return cart.reduce((sum, item) => {
//       // If value_type is 'P' (Percentage), calculate percentage of price
//       if (item.value_type === 'P') {
//         return sum + (item.price * item.quantity * item.value) / 100;
//       }
//       // If value_type is 'F' (Fixed), add fixed amount per item
//       else if (item.value_type === 'F') {
//         return sum + item.value * item.quantity;
//       }
//       // If no value_type, use 0 tax (don't hardcode)
//       return sum + 0;
//     }, 0);
//   };

//   const taxes = calculateTaxes();
//   const finalTotal = totalAmount + taxes;

//   // ✅ Get detailed tax breakdown for a single item - purely from API
//   const getItemTaxBreakdown = (item) => {
//     const quantity = item.quantity || 1;
//     const price = item.price || 0;
//     const value = item.value || 0;
//     const valueType = item.value_type || '';

//     if (valueType === 'P') {
//       const taxAmount = (price * quantity * value) / 100;
//       return {
//         type: 'percentage',
//         rate: value,
//         taxAmount: taxAmount,
//         cgst: taxAmount / 2,
//         sgst: taxAmount / 2,
//         fixedTax: 0,
//       };
//     } else if (valueType === 'F') {
//       const taxAmount = value * quantity;
//       return {
//         type: 'fixed',
//         rate: value,
//         taxAmount: taxAmount,
//         cgst: 0,
//         sgst: 0,
//         fixedTax: taxAmount,
//       };
//     } else {
//       // No tax if value_type is not specified
//       return {
//         type: 'none',
//         rate: 0,
//         taxAmount: 0,
//         cgst: 0,
//         sgst: 0,
//         fixedTax: 0,
//       };
//     }
//   };

//   // ✅ Calculate total tax breakdown across all items
//   const calculateTotalTaxBreakdown = () => {
//     let totalCGST = 0;
//     let totalSGST = 0;
//     let totalFixedTax = 0;
//     let totalPercentageTax = 0;

//     cart.forEach((item) => {
//       const breakdown = getItemTaxBreakdown(item);
//       totalCGST += breakdown.cgst;
//       totalSGST += breakdown.sgst;
//       totalFixedTax += breakdown.fixedTax;
//       totalPercentageTax += breakdown.taxAmount;
//     });

//     return {
//       cgst: totalCGST,
//       sgst: totalSGST,
//       fixedTax: totalFixedTax,
//       totalPercentageTax: totalPercentageTax,
//       totalTax: totalCGST + totalSGST + totalFixedTax,
//     };
//   };

//   const taxBreakdown = calculateTotalTaxBreakdown();

//   // ✅ Get tax display text for an item - purely from API
//   const getTaxDisplay = (item) => {
//     if (item.value_type === 'P') {
//       return `${item.value}%`;
//     } else if (item.value_type === 'F') {
//       return `₹${item.value}`;
//     } else {
//       return 'No Tax';
//     }
//   };

//   const handleCheckout = async () => {
//     if (!bookingId) {
//       alert('Booking ID not found. Please ensure you have a valid booking.');
//       return;
//     }

//     const items = cart.map((item) => ({
//       foodid: item.id,
//       quantity: item.quantity,
//     }));

//     try {
//       const orderResponse = await fetch(`${BQ_BASE_URL}/bq/api/orders/`, {
//         method: 'POST',
//         headers: {
//           accept: 'application/json',
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify({ bookingid: bookingId, items }),
//       });

//       const orderData = await orderResponse.json();
//       console.log('Order Response:', orderData);

//       if (!orderResponse.ok) {
//         throw new Error('Failed to place order');
//       }

//       const food_order_id = orderData.order_id;

//       const billingResponse = await fetch(
//         `${BQ_BASE_URL}/bq/api/create_food_order_billing?bookingid=${bookingId}&food_order_id=${food_order_id}`,
//         {
//           method: 'POST',
//           headers: {
//             accept: 'application/json',
//           },
//         }
//       );

//       const billingData = await billingResponse.json();
//       localStorage.setItem('billingDetails', JSON.stringify(billingData));
//       console.log('Billing Response:', billingData);

//       if (!billingResponse.ok) {
//         throw new Error('Failed to create billing');
//       }

//       toast.success('Order placed & billing done successfully!');
//       setCart([]);
//       setIsSidebarOpen(false);
//       setShowPaymentModal(true);
//     } catch (error) {
//       console.error('Error:', error);
//       alert('Checkout failed. Try again.');
//     }
//   };

//   useEffect(() => {
//     const loadRazorpayScript = () => {
//       const script = document.createElement('script');
//       script.src = 'https://checkout.razorpay.com/v1/checkout.js';
//       script.async = true;
//       script.onload = () => setRazorpayLoaded(true);
//       script.onerror = () => {
//         toast.error('Error loading Razorpay script. Please try again.');
//       };
//       document.body.appendChild(script);
//     };

//     loadRazorpayScript();
//   }, []);

//   const handlePaymentOption = async (paymentMethod) => {
//     if (!razorpayLoaded) {
//       toast.error('Razorpay script is not loaded yet.');
//       return;
//     }

//     if (paymentMethod === 'payNow') {
//       try {
//         const billingDetails = JSON.parse(localStorage.getItem('billingDetails'));
//         if (!billingDetails || !billingDetails.billingid) {
//           toast.error('Billing details not found. Please try again.');
//           return;
//         }

//         const billingId = billingDetails.billingid;
//         const primaryBooking = bookingDetails.primaryBooking || bookingDetails.bookings?.[0];

//         const apiUrl = `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order?billingid=${billingId}&bookingid=${bookingId}`;

//         const response = await fetch(apiUrl, {
//           method: 'POST',
//           headers: {
//             'Content-Type': 'application/json',
//             Accept: 'application/json',
//           },
//         });

//         if (!response.ok) throw new Error('Failed to create payment order');

//         const orderData = await response.json();

//         if (orderData && orderData.razorpay_order_id) {
//           const options = {
//             key: REACT_APP_RAZORPAY_KEY,
//             amount: orderData.amount,
//             currency: 'INR',
//             name: 'Pagoda Hotel',
//             description: 'Food Service',
//             order_id: orderData.razorpay_order_id,
//             handler: async function (response) {
//               const paymentDetails = {
//                 razorpay_order_id: response.razorpay_order_id,
//                 razorpay_payment_id: response.razorpay_payment_id,
//                 razorpay_signature: response.razorpay_signature,
//               };

//               try {
//                 const verificationResponse = await fetch(
//                   `${BQ_BASE_URL}/bq/api/razorpay/verify_payment`,
//                   {
//                     method: 'POST',
//                     headers: {
//                       'Content-Type': 'application/json',
//                       Accept: 'application/json',
//                     },
//                     body: JSON.stringify(paymentDetails),
//                   }
//                 );

//                 if (verificationResponse.ok) {
//                   toast.success('Payment successful and verified!');
//                   window.location.href =
//                     '/reservation/options/booking/welcome/food-service/succesful';
//                 } else {
//                   toast.error('Payment verification failed.');
//                 }
//               } catch (error) {
//                 console.error('Verification error:', error);
//                 toast.error('Error verifying payment. Please try again.');
//               }
//             },
//             prefill: {
//               name: primaryBooking?.guest_name || 'Guest',
//               email: primaryBooking?.guest_email || '',
//               contact: primaryBooking?.guest_phone?.toString() || '',
//             },
//             theme: {
//               color: '#3399cc',
//             },
//           };

//           const rzp = new window.Razorpay(options);
//           rzp.open();
//         }
//       } catch (error) {
//         console.error('Payment error:', error);
//         toast.error('Error processing payment. Please try again.');
//       }
//     } else if (paymentMethod === 'payLater') {
//       toast.info('Payment deferred. Please pay at the front desk.');
//       localStorage.removeItem('selectedService');
//       localStorage.removeItem('billingDetails');
//       setShowPaymentModal(false);
//     } else if (paymentMethod === 'billToCompany') {
//       toast.info(
//         `Bill to company option selected. Charges will be billed to ${guestData?.company_name || 'your company'}.`
//       );
//       localStorage.removeItem('selectedService');
//       localStorage.removeItem('billingDetails');
//       setShowPaymentModal(false);
//     }
//   };

//   const filteredItems = menuItems.filter((item) => {
//     const categoryid = getCategoryIdFromSubCategoryId(item.subCategoryId);
//     const matchesCategory = selectedCategory ? categoryid === selectedCategory : true;
//     const matchesSubcategory = selectedSubcategory
//       ? item.subCategoryId === selectedSubcategory
//       : true;
//     const matchesSearch = item.title.toLowerCase().includes(search.toLowerCase());

//     return matchesCategory && matchesSubcategory && matchesSearch;
//   });

//   if (loading) return <p className="text-center text-lg font-semibold">Loading...</p>;
//   if (error) return <p className="text-center text-red-500">{error}</p>;

//   return (
//     <div className="min-h-screen bg-gray-100 p-6 flex flex-col items-center ">
//       <Link
//         to="/reservation/options/booking/welcome/room-service"
//         className="absolute top-24 left-8 text-black hover:text-gray-800 transition font-semibold text-2xl"
//       >
//         <IoMdArrowRoundBack size={32} />
//       </Link>

//       <div className="fixed right-16 top-28 z-40">
//         <button
//           onClick={toggleSidebar}
//           className="relative text-black text-2xl bg-white p-2 rounded-full shadow-lg hover:shadow-xl transition"
//         >
//           <FaShoppingCart size={36} />
//           {cart.length > 0 && (
//             <span className="absolute -top-1 -right-2 bg-red-500 text-white text-sm font-semibold px-2 rounded-full py-1">
//               {cart.length}
//             </span>
//           )}
//         </button>
//       </div>

//       <header className="text-white p-6 text-center">
//         <h1 className="text-3xl font-bold text-black mt-">Food Menu</h1>
//         <p className="text-md font-semibold text-gray-700 mb-1">
//           Explore a variety of delicious meals, from hearty main courses to sweet desserts.
//         </p>
//         <hr className="border border-gray-300 w-2/3 mx-auto mb-3" />
//       </header>

//       <div className="relative w-full max-w-7xl my-6">
//         <input
//           type="text"
//           placeholder="Search for food..."
//           value={search}
//           onChange={(e) => setSearch(e.target.value)}
//           className="w-full px-12 py-2.5 border border-gray-300 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-gray-400"
//         />
//         <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500" />
//       </div>

//       <div className="flex gap-4 overflow-x-auto mb-4">
//         <button
//           className={`px-4 py-2 rounded-lg ${
//             selectedCategory === null ? 'bg-black text-white' : 'bg-gray-200'
//           }`}
//           onClick={() => {
//             setSelectedCategory(null);
//             setSelectedSubcategory(null);
//           }}
//         >
//           All
//         </button>
//         {categories.map((category) => (
//           <button
//             key={category.id}
//             className={`px-4 py-2 rounded-lg ${
//               selectedCategory === category.id ? 'bg-black text-white' : 'bg-gray-200'
//             }`}
//             onClick={() => {
//               setSelectedCategory(category.id);
//               setSelectedSubcategory(null);
//             }}
//           >
//             {category.name}
//           </button>
//         ))}
//       </div>

//       {selectedCategory && (
//         <div className="flex gap-4 overflow-x-auto mb-4">
//           <button
//             className={`px-4 py-2 rounded-lg ${
//               selectedSubcategory === null ? 'bg-black text-white' : 'bg-gray-200'
//             }`}
//             onClick={() => setSelectedSubcategory(null)}
//           >
//             All
//           </button>
//           {subcategories
//             .filter((sub) => sub.categoryid === selectedCategory)
//             .map((sub) => (
//               <button
//                 key={sub.id}
//                 className={`px-4 py-2 rounded-lg ${
//                   selectedSubcategory === sub.id ? 'bg-black text-white' : 'bg-gray-200'
//                 }`}
//                 onClick={() => {
//                   setSelectedSubcategory(sub.id);
//                 }}
//               >
//                 {sub.name}
//               </button>
//             ))}
//         </div>
//       )}

//       <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-2 max-w-7xl">
//         {filteredItems.map((item) => {
//           const isInCart = cart.some((cartItem) => cartItem.id === item.id);
//           return (
//             <div
//               key={item.id}
//               className="relative rounded-2xl overflow-hidden shadow-lg bg-white hover:shadow-xl transition-shadow flex flex-col"
//             >
//               <img src={item.image} alt={item.title} className="w-full h-60 object-cover" />
//               <div className="absolute top-2 right-2 bg-white p-1 rounded-full shadow-md">
//                 {item.foodType === 'Veg' ? (
//                   <FaLeaf className="text-green-600 text-xl" />
//                 ) : (
//                   <FaDrumstickBite className="text-red-600 text-xl" />
//                 )}
//               </div>
//               <div className="p-4 flex flex-col flex-grow">
//                 <div className="flex justify-between items-center">
//                   <h2 className="text-xl font-semibold">{item.title}</h2>
//                   <p
//                     className={`text-xs font-semibold px-3 py-1 rounded-lg ${
//                       item.isActive ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
//                     }`}
//                   >
//                     {item.isActive ? 'Available' : 'Unavailable'}
//                   </p>
//                 </div>
//                 <p className="text-gray-700 text-sm mb-3">{item.description}</p>
//                 <div className="flex flex-wrap gap-2 mt-auto mb-2">
//                   {item.mealTypes.map((meal) => (
//                     <span
//                       key={meal}
//                       className="bg-gray-200 text-gray-700 text-xs font-semibold px-3 py-1 rounded-full"
//                     >
//                       {meal}
//                     </span>
//                   ))}
//                 </div>
//                 <p className="text-lg font-bold mt-auto">₹{item.price}</p>
//                 <p className="text-xs text-gray-500">
//                   {item.value_type === 'P' ? (
//                     <span>+ {item.value}% Tax</span>
//                   ) : item.value_type === 'F' ? (
//                     <span>+ ₹{item.value} Fixed Tax</span>
//                   ) : (
//                     <span className="text-gray-400">No Tax</span>
//                   )}
//                 </p>
//               </div>
//               <div className="px-4 py-2">
//                 <button
//                   onClick={() => handleAddToCart(item)}
//                   disabled={!item.isActive}
//                   className={`w-full px-4 py-2 rounded-lg flex justify-center items-center gap-2 ${
//                     isInCart
//                       ? 'bg-green-600 text-white'
//                       : item.isActive
//                         ? 'bg-black text-white'
//                         : 'bg-gray-300 text-gray-700 font-semibold cursor-not-allowed'
//                   }`}
//                 >
//                   {isInCart ? (
//                     <>
//                       <FaCheck /> Added
//                     </>
//                   ) : (
//                     <>
//                       <FaShoppingCart /> Add to Cart
//                     </>
//                   )}
//                 </button>
//               </div>
//             </div>
//           );
//         })}
//       </div>

//       {isSidebarOpen && (
//         <div className="fixed right-0 top-0 h-full w-1/3 bg-white shadow-2xl p-6 overflow-y-auto z-50 transform transition-transform duration-300 ease-in-out flex flex-col">
//           <button
//             onClick={toggleSidebar}
//             className="text-gray-700 text-2xl absolute top-4 right-4 hover:text-gray-900"
//           >
//             <FaTimes />
//           </button>

//           <h2 className="text-2xl font-bold text-gray-800 mb-6">Your Cart</h2>

//           <div className="flex-grow overflow-y-auto px-4">
//             {cart.length > 0 ? (
//               cart.map((item) => {
//                 const itemTax = getItemTaxBreakdown(item);
//                 return (
//                   <div key={item.id} className="flex items-center gap-4 border-b pb-6 mb-4">
//                     <img
//                       src={item.image}
//                       alt={item.title}
//                       className="w-20 h-20 rounded-lg shadow-md object-cover"
//                     />
//                     <div className="flex-grow">
//                       <h3 className="text-[18px] font-semibold text-gray-900">{item.title}</h3>
//                       <p className="text-lg font-semibold text-gray-600">
//                         ₹{item.price * item.quantity}
//                       </p>
//                       <p className="text-xs text-gray-500">
//                         {item.value_type === 'P' ? (
//                           <span>
//                             Tax: {item.value}% (₹{itemTax.taxAmount.toFixed(2)})
//                           </span>
//                         ) : item.value_type === 'F' ? (
//                           <span>
//                             Tax: ₹{item.value} fixed (₹{itemTax.taxAmount.toFixed(2)})
//                           </span>
//                         ) : (
//                           <span className="text-gray-400">No Tax</span>
//                         )}
//                       </p>
//                     </div>

//                     <div className="flex flex-col items-center">
//                       <button
//                         onClick={() => handleRemoveFromCart(item.id)}
//                         className="mb-3 px-3 py-1 whitespace-nowrap text-sm font-semibold text-white bg-red-500 rounded-md hover:bg-red-700 hover:text-white transition"
//                       >
//                         Remove Item
//                       </button>
//                       <div className="flex items-center gap-2">
//                         <button
//                           onClick={() => handleDecreaseQuantity(item.id)}
//                           className="w-10 h-10 flex items-center justify-center bg-gray-200 rounded-full hover:bg-gray-300"
//                         >
//                           <FaMinus />
//                         </button>
//                         <span className="text-lg font-bold">{item.quantity}</span>
//                         <button
//                           onClick={() => handleIncreaseQuantity(item.id)}
//                           className="w-10 h-10 flex items-center justify-center bg-gray-200 rounded-full hover:bg-gray-300"
//                         >
//                           <FaPlus />
//                         </button>
//                       </div>
//                     </div>
//                   </div>
//                 );
//               })
//             ) : (
//               <p className="text-center text-gray-500">Your cart is empty.</p>
//             )}
//           </div>

//           {cart.length > 0 && (
//             <div className="mt-auto border-t pt-4 bg-white">
//               {/* Subtotal */}
//               <div className="flex justify-between mb-2">
//                 <span className="text-gray-600 font-semibold">Subtotal:</span>
//                 <span className="font-semibold">
//                   ₹
//                   {totalAmount.toLocaleString('en-IN', {
//                     minimumFractionDigits: 2,
//                     maximumFractionDigits: 2,
//                   })}
//                 </span>
//               </div>

//               {/* Taxes & Charges - Purely from API */}
//               <div className="flex justify-between mb-2 relative group">
//                 <span className="text-gray-600 font-semibold flex items-center gap-1">
//                   Taxes & Charges
//                   <span className="text-gray-400 cursor-pointer relative">
//                     <FaInfoCircle size={14} />
//                     {/* Tooltip */}
//                     <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-2 px-3 w-56 z-10">
//                       <div className="font-semibold mb-1 text-center">Tax Breakdown</div>
//                       {taxBreakdown.cgst > 0 && (
//                         <div className="flex justify-between">
//                           <span>CGST:</span>
//                           <span>
//                             ₹
//                             {taxBreakdown.cgst.toLocaleString('en-IN', {
//                               minimumFractionDigits: 2,
//                               maximumFractionDigits: 2,
//                             })}
//                           </span>
//                         </div>
//                       )}
//                       {taxBreakdown.sgst > 0 && (
//                         <div className="flex justify-between">
//                           <span>SGST:</span>
//                           <span>
//                             ₹
//                             {taxBreakdown.sgst.toLocaleString('en-IN', {
//                               minimumFractionDigits: 2,
//                               maximumFractionDigits: 2,
//                             })}
//                           </span>
//                         </div>
//                       )}
//                       {taxBreakdown.fixedTax > 0 && (
//                         <div className="flex justify-between">
//                           <span>Fixed Tax:</span>
//                           <span>
//                             ₹
//                             {taxBreakdown.fixedTax.toLocaleString('en-IN', {
//                               minimumFractionDigits: 2,
//                               maximumFractionDigits: 2,
//                             })}
//                           </span>
//                         </div>
//                       )}
//                       <div className="border-t border-gray-600 mt-1 pt-1 flex justify-between font-semibold">
//                         <span>Total Tax:</span>
//                         <span>
//                           ₹
//                           {taxBreakdown.totalTax.toLocaleString('en-IN', {
//                             minimumFractionDigits: 2,
//                             maximumFractionDigits: 2,
//                           })}
//                         </span>
//                       </div>
//                       {/* Show per-item tax details */}
//                       {cart.length > 0 && (
//                         <div className="border-t border-gray-600 mt-1 pt-1">
//                           <div className="font-semibold text-center text-[10px]">Per Item</div>
//                           {cart.map((item, idx) => {
//                             const tax = getItemTaxBreakdown(item);
//                             return (
//                               <div key={idx} className="flex justify-between text-[10px]">
//                                 <span className="truncate max-w-[100px]">{item.title}:</span>
//                                 <span>
//                                   ₹{tax.taxAmount.toFixed(2)} ({getTaxDisplay(item)})
//                                 </span>
//                               </div>
//                             );
//                           })}
//                         </div>
//                       )}
//                     </div>
//                   </span>
//                 </span>
//                 <span className="font-semibold">
//                   ₹
//                   {taxBreakdown.totalTax.toLocaleString('en-IN', {
//                     minimumFractionDigits: 2,
//                     maximumFractionDigits: 2,
//                   })}
//                 </span>
//               </div>

//               {/* Line separator */}
//               <hr className="my-2 border-gray-300" />

//               {/* Total */}
//               <div className="flex justify-between mb-4">
//                 <span className="text-lg font-bold text-gray-800">Total:</span>
//                 <span className="text-lg font-bold text-gray-800">
//                   ₹
//                   {finalTotal.toLocaleString('en-IN', {
//                     minimumFractionDigits: 2,
//                     maximumFractionDigits: 2,
//                   })}
//                 </span>
//               </div>

//               <button
//                 onClick={handleCheckout}
//                 className="mt-4 w-full bg-black text-white py-3 rounded-lg text-lg font-semibold hover:bg-gray-900 transition"
//               >
//                 Order Now
//               </button>
//             </div>
//           )}
//         </div>
//       )}

//       {showPaymentModal && (
//         <div className="fixed inset-0 flex justify-center items-center bg-gray-800 bg-opacity-70 z-50">
//           <div className="bg-white p-6 rounded-lg w-4/12 text-center shadow-md transform transition-all duration-200">
//             <h3 className="text-2xl font-semibold mb-4 text-gray-900">Complete Your Payment</h3>
//             <p className="text-md text-gray-600 mb-6">
//               Your Food has been successfully Ordered! Please select a payment method to proceed.
//             </p>

//             <div className="flex justify-center gap-4">
//               {/* Pay Later option for leisure guests */}
//               {!isCorporateGuest && (
//                 <button
//                   onClick={() => handlePaymentOption('payLater')}
//                   className="bg-black text-white px-10 py-2 rounded-md hover:text-gray-200 transition-all duration-200 font-semibold"
//                 >
//                   Pay Later
//                 </button>
//               )}

//               {/* Bill to Company option for corporate guests */}
//               {isCorporateGuest && (
//                 <button
//                   onClick={() => handlePaymentOption('billToCompany')}
//                   className="bg-black text-white px-10 py-2 rounded-md hover:text-gray-200 transition-all duration-200 font-semibold"
//                 >
//                   Bill to Company
//                 </button>
//               )}

//               {/* Pay Now option for all guests */}
//               <button
//                 onClick={() => handlePaymentOption('payNow')}
//                 className="bg-green-600 text-white px-10 py-2 rounded-md hover:text-black transition-all duration-200 font-semibold"
//               >
//                 Pay Now
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//       <ToastContainer />
//     </div>
//   );
// }

// export default Food;
